import { initializeApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';
import {
  initializeAuth,
  setPersistence,
  inMemoryPersistence,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  browserPopupRedirectResolver,
} from 'firebase/auth';

import { getSafeErrorIdentifier } from './components/utils/getSafeErrorIdentifier';

const config = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT,
  storageBucket: process.env.REACT_APP_STORAGE,
  messagingSenderId: process.env.REACT_APP_MESSAGING,
  appId: process.env.REACT_APP_APP_ID,
};

// CRA only injects REACT_APP_-prefixed vars, so these optional fields stay out.
if (process.env.DATABASE_URL) {
  config.databaseURL = process.env.DATABASE_URL;
}
if (process.env.MEASUREMENT_ID) {
  config.measurementId = process.env.MEASUREMENT_ID;
}

export const app = initializeApp(config);

// Shared by every getAuth() in the app. inMemoryPersistence goes first so new
// sessions stay off disk; the stores after it are temporary and only let
// Firebase find a session left by an older build. The resolver is required by
// signInWithPopup and signInWithRedirect.
const auth = initializeAuth(app, {
  persistence: [
    inMemoryPersistence,
    indexedDBLocalPersistence,
    browserLocalPersistence,
    browserSessionPersistence,
  ],
  popupRedirectResolver: browserPopupRedirectResolver,
});

// Moves a session found in those stores to memory, so the refresh token is no
// longer kept on disk.
setPersistence(auth, inMemoryPersistence).catch(error => {
  console.error('firebase.setPersistence:', getSafeErrorIdentifier(error));
});

// Public reCAPTCHA Enterprise site key, per environment. Empty leaves App Check
// off (appCheck = null); Firebase Auth runs in Monitoring mode either way.
const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

// Enable the App Check debug token on the hosts listed in the env (comma-separated).
// `window` is the SDK's `self`.
const debugHosts = (process.env.REACT_APP_APPCHECK_DEBUG_HOSTS || '')
  .split(',')
  .map(host => host.trim())
  .filter(Boolean);

const isLocalDevelopment =
  typeof window !== 'undefined' &&
  debugHosts.includes(window.location.hostname);

if (
  isLocalDevelopment &&
  process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG === 'true'
) {
  window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}

export const appCheck = siteKey
  ? initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(siteKey),
      isTokenAutoRefreshEnabled: true,
    })
  : null;
